import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(".devices-overview");
  if (!wrapper) return;

  const header = document.querySelector(".page-header");
  const tabListWrap = document.querySelector(".devices-overview__tab-list-wrap");
  const leftSticky = document.querySelector(".devices-overview__column--left");
  const rightSticky = document.querySelector(".devices-overview__column--right");

  const productWrappers = gsap.utils.toArray(".devices-overview__product-wrapper");
  const leftContents = gsap.utils.toArray(".devices-overview__left-content");
  const rightContents = gsap.utils.toArray(".devices-overview__right-content");
  const imagesAll = gsap.utils.toArray(".devices-overview__main-image");

  if (!productWrappers.length) return;

  // Helper: sticky offsets
  const getOffsets = () => {
    const headerH = header ? header.offsetHeight : 0;
    const tabsH = tabListWrap ? tabListWrap.offsetHeight : 0;
    return { headerH, tabsH, stickyTop: headerH + tabsH };
  };

  // Smooth scroll for tab anchors
  document.querySelectorAll("a.devices-overview__tab-link").forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (!href || !href.startsWith("#")) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      const { stickyTop } = getOffsets();
      const rect = target.getBoundingClientRect();
      const y = rect.top + window.pageYOffset - (window.innerHeight / 2 - rect.height / 2);
      gsap.to(window, { duration: 0.6, scrollTo: y, ease: "power2.out", overwrite: "auto" });
    });
  });

  // Toggle active classes
  const setActiveById = (id) => {
    leftContents.forEach((el) => el.classList.toggle("active", el.dataset.product === id));
    rightContents.forEach((el) => el.classList.toggle("active", el.dataset.product === id));
    document.querySelectorAll("a.devices-overview__tab-link").forEach((a) => {
      const cmp = a.getAttribute("href")?.replace("#", "");
      a.classList.toggle("active", cmp === id);
    });
    productWrappers.forEach((w) => w.classList.toggle("active", w.id === id));
  };

  let currentActiveId = null;
  const crossfadeToId = (id) => {
    if (!id || id === currentActiveId) return;
  
    const isLast = productWrappers[productWrappers.length - 1]?.id === id;
  
    gsap.to(
      `.devices-overview__left-content:not([data-product="${id}"]), .devices-overview__right-content:not([data-product="${id}"])`,
      { autoAlpha: 0, duration: isLast ? 0.05 : 0.25, overwrite: "auto" }
    );
    gsap.to(
      `.devices-overview__left-content[data-product="${id}"], .devices-overview__right-content[data-product="${id}"]`,
      { autoAlpha: 1, duration: isLast ? 0.1 : 0.35, overwrite: "auto" }
    );
  
    setActiveById(id);
    currentActiveId = id;
  };

  const setStickyOffsets = () => {
    const { headerH, stickyTop } = getOffsets();
    if (tabListWrap) tabListWrap.style.top = `${headerH}px`;
    if (leftSticky) leftSticky.style.top = `${stickyTop}px`;
    if (rightSticky) rightSticky.style.top = `${stickyTop}px`;
  };

  const calculateHeights = () => {
    const leftMax = leftContents.length ? Math.max(...leftContents.map(c => c.scrollHeight)) : 0;
    const rightMax = rightContents.length ? Math.max(...rightContents.map(c => c.scrollHeight)) : 0;
    const imgMax = imagesAll.length ? Math.max(...imagesAll.map(i => i.offsetHeight)) : 0;
    const extra = 325;
    const itemHeight = Math.max(leftMax, rightMax, imgMax) + extra;

    if (leftSticky) leftSticky.style.height = `${itemHeight}px`;
    if (rightSticky) rightSticky.style.height = `${itemHeight}px`;

    productWrappers.forEach((w) => w.style.height = `${itemHeight}px`);
    setStickyOffsets();
  };

  // Wait for images
  let pending = imagesAll.length;
  const refreshWhenReady = () => {
    if (--pending <= 0) {
      calculateHeights();
      ScrollTrigger.refresh();
    }
  };
  imagesAll.forEach((img) => {
    if (img.complete) refreshWhenReady();
    else {
      img.addEventListener("load", refreshWhenReady, { once: true });
      img.addEventListener("error", refreshWhenReady, { once: true });
    }
  });
  if (pending === 0) { calculateHeights(); ScrollTrigger.refresh(); }

  const mm = gsap.matchMedia();
  mm.add("(min-width: 992px)", () => {
    gsap.set([...leftContents, ...rightContents], { autoAlpha: 0 });
    if (productWrappers[0]?.id) crossfadeToId(productWrappers[0].id);

    productWrappers.forEach((wrap, index) => {
      const id = wrap.id;
      const img = wrap.querySelector(".devices-overview__main-image") || wrap;
      const title = wrap.querySelector(".devices-overview__product-large-text");

      // ScrollTrigger for active item by center
      ScrollTrigger.create({
        trigger: wrap,
        start: "top bottom",
        end: "bottom top",
        onUpdate: () => {
          const rect = img.getBoundingClientRect();
          const center = window.innerHeight / 2;
          const imgCenter = rect.top + rect.height / 2;
          if (Math.abs(center - imgCenter) < rect.height / 2) crossfadeToId(id);
        },
      });

      // Parallax large text: opposite direction
      if (title) {
        const wrapWidth = wrap.offsetWidth;
        const textWidth = title.offsetWidth;

        const isLast = index === productWrappers.length - 1;
        const endValue = isLast ? "bottom 84.5%" : "bottom 40%";
      
        gsap.fromTo(
          title,
          { x: 0 },
          {
            x: -wrapWidth - textWidth,
            ease: "none",
            scrollTrigger: {
              trigger: wrap,
              start: "top bottom",
              end: endValue,
              scrub: true,
            },
          }
        );
      }      
    });

    const onResize = () => { calculateHeights(); ScrollTrigger.refresh(); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  });

  mm.add("(max-width: 991px)", () => {
    gsap.set([...productWrappers, ...leftContents, ...rightContents], { autoAlpha: 1 });
  });

  // Global ScrollTrigger for smooth active switching
  const updateActiveRAF = () => {
    let raf = 0;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = window.innerHeight / 2;
        let closest = null;
        let dist = Infinity;
        productWrappers.forEach((wrap) => {
          const img = wrap.querySelector(".devices-overview__main-image");
          if (!img) return;
          const rect = img.getBoundingClientRect();
          const imgCenter = rect.top + rect.height / 2;
          const d = Math.abs(center - imgCenter);
          if (d < dist) { dist = d; closest = wrap; }
        });
        if (closest?.id) crossfadeToId(closest.id);
      });
    };
    window.addEventListener("scroll", update);
  };
  updateActiveRAF();

  calculateHeights();
  setStickyOffsets();
  ScrollTrigger.refresh();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => { calculateHeights(); ScrollTrigger.refresh(); });
  }
});
